import { createContext, useContext } from "react";

import { ProBuyerWorkspaceQuery } from "@/Apollo/schema";

/*
 * WHO-6629. The workspace context on its own, with no Apollo document behind
 * it: shared components (every product card heart, the PDP, the cart) read it
 * on every page, so importing it must cost nothing and must never pull
 * GraphQL documents into their bundles or their tests. The provider lives in
 * ProWorkspaceProvider.tsx (mounted in _app, gates only); the lazily loaded
 * WorkspaceEngine.tsx fills it for Pro Buyers in the gate.
 */

type Workspace = ProBuyerWorkspaceQuery["proBuyerWorkspace"];
export type WorkspaceProject = Workspace["projects"][number];
export type LastUsedProject = NonNullable<Workspace["activeProject"]>;

export type SaveMenuState = {
  productId: string;
  anchor: HTMLElement | null;
  /** "saved": opened by a heart that just saved; "manage": opened on a piece that already is a favorite. */
  mode: "saved" | "manage";
};

export type ConfirmState = {
  projectNames: string[];
  resolve: (confirmed: boolean) => void;
};

export type ProWorkspaceContextValue = {
  /** Gate on and FULL or READ_ONLY access: workspace surfaces may render. */
  enabled: boolean;
  /** FULL: may save, move, share. */
  canEdit: boolean;
  /**
   * WHO-6643 / F1. The project (and room) the designer last saved a piece
   * into from the save menu. No mode and nothing in the header: the save menu
   * pre-ticks it as "Laatst gebruikt" after every heart.
   */
  lastUsedProject: LastUsedProject | null;
  lastUsedRoomId: string | null;
  projects: WorkspaceProject[];
  workspaceLoading: boolean;
  /** Remembers the last used project and room (null forgets), silently: no toast. */
  rememberLastUsed: (projectId: string | null, roomId?: string | null) => void;
  /**
   * R1. Call after a heart saved a favorite: saves it into the last used
   * project too (pre-ticked) and opens the menu under the heart.
   */
  afterFavoriteSaved: (productId: string, anchor: HTMLElement | null) => void;
  /** Opens the same menu without touching the heart (PDP chip, Favorieten "+ Aan project"). */
  openSaveMenu: (productId: string, anchor: HTMLElement | null) => void;
  /**
   * R2. Resolves true when the heart may be emptied: at once when the piece is
   * in no project, else after "Overal verwijderen".
   */
  confirmUnfavorite: (productId: string) => Promise<boolean>;
};

export const DISABLED_WORKSPACE: ProWorkspaceContextValue = {
  enabled: false,
  canEdit: false,
  lastUsedProject: null,
  lastUsedRoomId: null,
  projects: [],
  workspaceLoading: false,
  rememberLastUsed: () => undefined,
  afterFavoriteSaved: () => undefined,
  openSaveMenu: () => undefined,
  confirmUnfavorite: async () => true,
};

export const ProWorkspaceContext =
  createContext<ProWorkspaceContextValue>(DISABLED_WORKSPACE);

/** Read the workspace from anywhere under `_app`. Disabled (all no-ops) for everyone outside the gate. */
export const useProWorkspace = () => useContext(ProWorkspaceContext);
